import { create } from "zustand";
import { devtools } from "zustand/middleware";

import {
  addToCart,
  clearCart,
  getCart,
  removeFromCart,
  updateCartItem,
} from "@/api/endpoints/purchase-flow/cart";
import { getServiceName } from "@/api/endpoints/services";
import { AuthGroup, CartItem, ServiceDetailAnswer } from "@/api/types";
import { captureException, captureMessage } from "@/lib/errors";
import { useAuthStore } from "@/stores/authStore";

interface PatientCart {
  authGroups: AuthGroup[];
  loading: boolean;
  error: string | null;
}

interface CartState {
  carts: Record<number, PatientCart>;
  currentPatientId: number | null;
  patientIdsWithCarts: Set<number>;
  quantityUpdateError: QuantityUpdateError | null;
}

interface CartActions {
  addItem: (
    service_id: number,
    quantity: number,
    payor_auth_id: number,
    serviceDetailAnswers?: ServiceDetailAnswer[],
  ) => Promise<number>;
  removeItem: (cart_item_id: number) => Promise<void>;
  updateItemQuantity: (cart_item_id: number, quantity: number) => void;
  clearCart: () => Promise<void>;
  setPatientCart: (patient_id: number) => Promise<void>;
  clearContext: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  loadCartFromServer: () => Promise<void>;
  getTotalPoints: () => number;
  getItemCount: () => number;
  hasItems: () => boolean;
  getCurrentCart: () => PatientCart | null;
  getAllItems: () => CartItem[];
  getPatientId: () => number | null;
  refreshAllCarts: () => Promise<void>;
  getPatientIdsWithCarts: () => number[];
  initializeCartsOnLogin: () => Promise<void>;
  clearAllCarts: () => void;
  clearQuantityUpdateError: () => void;
}

export type CartStore = CartState & CartActions;

const PATIENT_IDS_WITH_CARTS_KEY = "cart_patient_ids";
const CART_CONTEXT_KEY = "cart_context";

interface PersistedCartContext {
  currentPatientId: number | null;
}

const loadCartContext = (): PersistedCartContext | null => {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(CART_CONTEXT_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as PersistedCartContext;
  } catch (error) {
    captureException(error, {
      tags: { context: "loadCartContext" },
    });
    return null;
  }
};

const saveCartContext = (context: PersistedCartContext): void => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CART_CONTEXT_KEY, JSON.stringify(context));
  } catch (error) {
    captureException(error, {
      tags: { context: "saveCartContext" },
    });
  }
};

const clearCartContext = (): void => {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(CART_CONTEXT_KEY);
  } catch (error) {
    captureException(error, {
      tags: { context: "clearCartContext" },
    });
  }
};

const loadPatientIdsWithCarts = (): Set<number> => {
  if (typeof window === "undefined") return new Set<number>();
  try {
    const stored = localStorage.getItem(PATIENT_IDS_WITH_CARTS_KEY);
    if (stored) {
      const ids = JSON.parse(stored) as number[];
      return new Set(ids);
    }
  } catch (error) {
    captureException(error, {
      tags: { context: "loadPatientIdsWithCarts" },
    });
  }
  return new Set<number>();
};

const savePatientIdsWithCarts = (patientIds: Set<number>): void => {
  if (typeof window === "undefined") return;
  try {
    const ids = Array.from(patientIds);
    localStorage.setItem(PATIENT_IDS_WITH_CARTS_KEY, JSON.stringify(ids));
  } catch (error) {
    captureException(error, {
      tags: { context: "savePatientIdsWithCarts" },
    });
  }
};

const savedCartContext = loadCartContext();

const initialState: CartState = {
  carts: {},
  currentPatientId: savedCartContext?.currentPatientId ?? null,
  patientIdsWithCarts: loadPatientIdsWithCarts(),
  quantityUpdateError: null,
};

const getCurrentCart = (state: CartState): PatientCart | null => {
  if (!state.currentPatientId) return null;
  return state.carts[state.currentPatientId] || null;
};

const createEmptyCart = (): PatientCart => ({
  authGroups: [],
  loading: false,
  error: null,
});

const getAllItemsFromGroups = (authGroups: AuthGroup[]): CartItem[] =>
  authGroups.flatMap((group) => group.services);

const getItemId = (item: CartItem): number => {
  const id = item.cart_item_id ?? item.item_id;
  if (!id) {
    throw new Error("Cart item is missing both cart_item_id and item_id");
  }
  return id;
};

let debouncedReloadTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_DELAY_MS = 800;
const QUANTITY_UPDATE_DEBOUNCE_MS = 300;

const debouncedReloadCart = (loadCartFromServer: () => Promise<void>) => {
  if (debouncedReloadTimer) {
    clearTimeout(debouncedReloadTimer);
  }

  debouncedReloadTimer = setTimeout(() => {
    debouncedReloadTimer = null;
    loadCartFromServer().catch((reloadError) => {
      captureException(reloadError, {
        tags: { context: "debouncedReloadCart" },
      });
    });
  }, DEBOUNCE_DELAY_MS);
};

export interface QuantityUpdateError {
  cartItemId: number;
  message: string;
  adjustedQuantity?: number;
}

interface PendingQuantityUpdate {
  timer: ReturnType<typeof setTimeout>;
  quantity: number;
  originalQuantity: number;
  execute: () => Promise<void>;
}

const pendingQuantityUpdates = new Map<number, PendingQuantityUpdate>();

export const flushPendingQuantityUpdates = () => {
  pendingQuantityUpdates.forEach((pending) => {
    clearTimeout(pending.timer);
    pending.execute();
  });
};

export const useCartStore = create<CartStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      addItem: async (service_id, quantity, payor_auth_id, serviceDetailAnswers) => {
        const state = get();
        const currentCart = getCurrentCart(state);
        const tenantDb = useAuthStore.getState().tenant;

        if (!currentCart || currentCart.loading) {
          throw new Error("Cart is loading.");
        }

        if (!state.currentPatientId) {
          captureMessage("Patient ID is missing", {
            tags: { context: "cart/addItem" },
          });
          throw new Error("Patient ID is missing");
        }

        const ctxPatient = state.currentPatientId;

        if (!tenantDb) {
          throw new Error("Tenant database not found");
        }

        try {
          set(
            (state) => {
              if (!state.currentPatientId) return state;
              const cart = state.carts[state.currentPatientId] || createEmptyCart();
              return {
                ...state,
                carts: {
                  ...state.carts,
                  [state.currentPatientId]: {
                    ...cart,
                    loading: true,
                    error: null,
                  },
                },
              };
            },
            undefined,
            "cart/addItem - start",
          );

          const addResult = await addToCart(
            {
              patient_id: ctxPatient,
              service_id,
              quantity,
              payor_auth_id,
              ...(serviceDetailAnswers?.length && { service_detail_answers: serviceDetailAnswers }),
            },
            tenantDb,
          );

          if (get().currentPatientId !== ctxPatient) return addResult.cart_item_id;

          set(
            (state) => {
              const newPatientIdsWithCarts = new Set(state.patientIdsWithCarts);
              newPatientIdsWithCarts.add(ctxPatient);
              savePatientIdsWithCarts(newPatientIdsWithCarts);
              return {
                ...state,
                patientIdsWithCarts: newPatientIdsWithCarts,
              };
            },
            undefined,
            "cart/addItem - track patient",
          );

          await get().loadCartFromServer();
          set(
            (state) => {
              if (!state.currentPatientId) return state;
              const cart = state.carts[state.currentPatientId];
              if (!cart) return state;
              return {
                ...state,
                carts: {
                  ...state.carts,
                  [state.currentPatientId]: {
                    ...cart,
                    loading: false,
                  },
                },
              };
            },
            undefined,
            "cart/addItem - success",
          );
          return addResult.cart_item_id;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Failed to add item to cart";
          set(
            (state) => {
              if (!state.currentPatientId) return state;
              const cart = state.carts[state.currentPatientId];
              if (!cart) return state;
              return {
                ...state,
                carts: {
                  ...state.carts,
                  [state.currentPatientId]: {
                    ...cart,
                    loading: false,
                    error: errorMessage,
                  },
                },
              };
            },
            undefined,
            "cart/addItem - error",
          );
          throw error;
        }
      },

      removeItem: async (cart_item_id) => {
        const state = get();
        const currentCart = getCurrentCart(state);

        if (!state.currentPatientId || !currentCart || currentCart.loading) return;

        const ctxPatient = state.currentPatientId;
        const tenantDb = useAuthStore.getState().tenant;

        if (!tenantDb) {
          set(
            (state) => {
              if (!state.currentPatientId) return state;
              const cart = state.carts[state.currentPatientId];
              if (!cart) return state;
              return {
                ...state,
                carts: {
                  ...state.carts,
                  [state.currentPatientId]: {
                    ...cart,
                    error: "Tenant database not found",
                    loading: false,
                  },
                },
              };
            },
            undefined,
            "cart/removeItem - no tenant",
          );
          return;
        }

        try {
          set(
            (state) => {
              if (!state.currentPatientId) return state;
              const cart = state.carts[state.currentPatientId];
              if (!cart) return state;
              return {
                ...state,
                carts: {
                  ...state.carts,
                  [state.currentPatientId]: {
                    ...cart,
                    loading: true,
                    error: null,
                  },
                },
              };
            },
            undefined,
            "cart/removeItem - start",
          );

          await removeFromCart(cart_item_id, tenantDb);

          if (get().currentPatientId !== ctxPatient) return;

          await get().loadCartFromServer();
        } catch (error) {
          captureException(error, {
            tags: { context: "cart/removeItem" },
          });
          set(
            (state) => {
              if (!state.currentPatientId) return state;
              const cart = state.carts[state.currentPatientId];
              if (!cart) return state;
              return {
                ...state,
                carts: {
                  ...state.carts,
                  [state.currentPatientId]: {
                    ...cart,
                    loading: false,
                  },
                },
              };
            },
            undefined,
            "cart/removeItem - error",
          );
          throw error;
        }
      },

      updateItemQuantity: (cart_item_id, quantity) => {
        const state = get();
        const currentCart = getCurrentCart(state);

        if (!state.currentPatientId || !currentCart || currentCart.loading) return;

        const ctxPatient = state.currentPatientId;
        const tenantDb = useAuthStore.getState().tenant;

        if (!tenantDb) {
          set(
            (state) => {
              if (!state.currentPatientId) return state;
              const cart = state.carts[state.currentPatientId];
              if (!cart) return state;
              return {
                ...state,
                carts: {
                  ...state.carts,
                  [state.currentPatientId]: {
                    ...cart,
                    error: "Tenant database not found",
                    loading: false,
                  },
                },
              };
            },
            undefined,
            "cart/updateItemQuantity - no tenant",
          );
          return;
        }

        const allItems = getAllItemsFromGroups(currentCart.authGroups);
        const itemToUpdate = allItems.find((item) => getItemId(item) === cart_item_id);
        if (!itemToUpdate) return;

        const pointsPerItem = itemToUpdate.points || 0;
        const optimisticTotalPoints = pointsPerItem * Number(quantity);

        const pending = pendingQuantityUpdates.get(cart_item_id);
        const originalQuantity = pending?.originalQuantity ?? itemToUpdate.quantity;

        if (pending) {
          clearTimeout(pending.timer);
        }

        const mapServicesInGroups = (
          groups: AuthGroup[],
          mapper: (item: CartItem) => CartItem,
        ): AuthGroup[] =>
          groups.map((group) => ({
            ...group,
            services: group.services.map(mapper),
          }));

        set(
          (state) => {
            if (!state.currentPatientId) return state;
            const cart = state.carts[state.currentPatientId];
            if (!cart) return state;
            const updatedGroups = mapServicesInGroups(cart.authGroups, (item) =>
              getItemId(item) === cart_item_id
                ? {
                    ...item,
                    quantity: Number(quantity),
                    total_points: Number(optimisticTotalPoints),
                    points: Number(pointsPerItem),
                  }
                : item,
            ).map((group) => ({
              ...group,
              total: group.services.reduce((sum, s) => sum + (s.total_points || 0), 0),
            }));
            return {
              ...state,
              quantityUpdateError: null,
              carts: {
                ...state.carts,
                [state.currentPatientId]: {
                  ...cart,
                  authGroups: updatedGroups,
                  error: null,
                },
              },
            };
          },
          undefined,
          "cart/updateItemQuantity - optimistic",
        );

        const executeUpdate = async () => {
          pendingQuantityUpdates.delete(cart_item_id);

          try {
            await updateCartItem(cart_item_id, { quantity }, tenantDb);

            if (get().currentPatientId !== ctxPatient) return;

            debouncedReloadCart(get().loadCartFromServer);
          } catch (error) {
            captureException(error, {
              tags: { context: "cart/updateItemQuantity" },
            });

            if (debouncedReloadTimer) {
              clearTimeout(debouncedReloadTimer);
              debouncedReloadTimer = null;
            }

            const errorData = (
              error as { response?: { data?: { errors?: string[]; available_points?: number } } }
            )?.response?.data;
            const errorMessage = errorData?.errors?.[0] || "";
            const insufficientMatch = errorMessage.match(/insufficient\s+points:\s*([\d.]+)\s*available/i);

            let revertQuantity = originalQuantity;
            let adjustedQuantity: number | undefined;

            if (insufficientMatch && pointsPerItem > 0) {
              const availablePoints = parseFloat(insufficientMatch[1]);
              const maxQuantity = Math.floor(availablePoints / pointsPerItem);
              revertQuantity = Math.max(1, maxQuantity);
              adjustedQuantity = revertQuantity;
            }

            set(
              (state) => {
                if (!state.currentPatientId) return state;
                const cart = state.carts[state.currentPatientId];
                if (!cart) return state;
                return {
                  ...state,
                  quantityUpdateError: {
                    cartItemId: cart_item_id,
                    message: errorMessage || "Failed to update quantity",
                    adjustedQuantity,
                  },
                  carts: {
                    ...state.carts,
                    [state.currentPatientId]: {
                      ...cart,
                      authGroups: mapServicesInGroups(cart.authGroups, (item) =>
                        getItemId(item) === cart_item_id
                          ? {
                              ...item,
                              quantity: revertQuantity,
                              total_points: pointsPerItem * revertQuantity,
                            }
                          : item,
                      ),
                    },
                  },
                };
              },
              undefined,
              "cart/updateItemQuantity - error revert",
            );

            get()
              .loadCartFromServer()
              .catch((reloadError) => {
                captureException(reloadError, {
                  tags: { context: "updateItemQuantity" },
                });
              });
          }
        };

        const timer = setTimeout(() => {
          executeUpdate();
        }, QUANTITY_UPDATE_DEBOUNCE_MS);

        pendingQuantityUpdates.set(cart_item_id, {
          timer,
          quantity,
          originalQuantity,
          execute: executeUpdate,
        });
      },

      clearCart: async () => {
        const state = get();
        const currentCart = getCurrentCart(state);

        if (!state.currentPatientId || !currentCart || currentCart.loading) return;

        const ctxPatient = state.currentPatientId;
        const tenantDb = useAuthStore.getState().tenant;

        if (!tenantDb) {
          set(
            (state) => {
              if (!state.currentPatientId) return state;
              const cart = state.carts[state.currentPatientId];
              if (!cart) return state;
              return {
                ...state,
                carts: {
                  ...state.carts,
                  [state.currentPatientId]: {
                    ...cart,
                    error: "Tenant database not found",
                    loading: false,
                  },
                },
              };
            },
            undefined,
            "cart/clearCart - no tenant",
          );
          return;
        }

        try {
          set(
            (state) => {
              if (!state.currentPatientId) return state;
              const cart = state.carts[state.currentPatientId];
              if (!cart) return state;
              return {
                ...state,
                carts: {
                  ...state.carts,
                  [state.currentPatientId]: {
                    ...cart,
                    loading: true,
                    error: null,
                  },
                },
              };
            },
            undefined,
            "cart/clearCart - start",
          );

          await clearCart(ctxPatient, tenantDb);

          if (get().currentPatientId !== ctxPatient) return;

          set(
            (state) => {
              if (!state.currentPatientId) return state;
              const cart = state.carts[state.currentPatientId];
              if (!cart) return state;
              const newPatientIdsWithCarts = new Set(state.patientIdsWithCarts);
              newPatientIdsWithCarts.delete(state.currentPatientId);
              savePatientIdsWithCarts(newPatientIdsWithCarts);

              saveCartContext({
                currentPatientId: null,
              });

              return {
                ...state,
                patientIdsWithCarts: newPatientIdsWithCarts,
                carts: {
                  ...state.carts,
                  [state.currentPatientId]: {
                    ...cart,
                    authGroups: [],
                    loading: false,
                  },
                },
              };
            },
            undefined,
            "cart/clearCart - success",
          );
        } catch (error) {
          captureException(error, {
            tags: { context: "cart/clearCart" },
          });
          set(
            (state) => {
              if (!state.currentPatientId) return state;
              const cart = state.carts[state.currentPatientId];
              if (!cart) return state;
              return {
                ...state,
                carts: {
                  ...state.carts,
                  [state.currentPatientId]: {
                    ...cart,
                    loading: false,
                  },
                },
              };
            },
            undefined,
            "cart/clearCart - error",
          );
          throw error;
        }
      },

      setPatientCart: async (patient_id) => {
        set(
          (state) => {
            const cart = state.carts[patient_id] || createEmptyCart();
            const newPatientIdsWithCarts = new Set(state.patientIdsWithCarts);
            newPatientIdsWithCarts.add(patient_id);
            savePatientIdsWithCarts(newPatientIdsWithCarts);

            saveCartContext({
              currentPatientId: patient_id,
            });

            return {
              ...state,
              currentPatientId: patient_id,
              patientIdsWithCarts: newPatientIdsWithCarts,
              carts: {
                ...state.carts,
                [patient_id]: {
                  ...cart,
                  loading: true,
                  error: null,
                },
              },
            };
          },
          undefined,
          "cart/setPatientCart",
        );

        await get().loadCartFromServer();
      },

      clearContext: () => {
        if (debouncedReloadTimer) {
          clearTimeout(debouncedReloadTimer);
          debouncedReloadTimer = null;
        }
        set(
          (state) => ({
            currentPatientId: null,
            carts: state.carts,
            patientIdsWithCarts: state.patientIdsWithCarts,
          }),
          undefined,
          "cart/clearContext",
        );
      },

      setLoading: (loading) => {
        set(
          (state) => {
            if (!state.currentPatientId) return state;
            const cart = state.carts[state.currentPatientId];
            if (!cart) return state;
            return {
              ...state,
              carts: {
                ...state.carts,
                [state.currentPatientId]: {
                  ...cart,
                  loading,
                },
              },
            };
          },
          undefined,
          "cart/setLoading",
        );
      },

      setError: (error) => {
        set(
          (state) => {
            if (!state.currentPatientId) return state;
            const cart = state.carts[state.currentPatientId];
            if (!cart) return state;
            return {
              ...state,
              carts: {
                ...state.carts,
                [state.currentPatientId]: {
                  ...cart,
                  error,
                },
              },
            };
          },
          undefined,
          "cart/setError",
        );
      },

      loadCartFromServer: async () => {
        const state = get();

        if (!state.currentPatientId) return;

        const ctxPatient = state.currentPatientId;
        const tenantDb = useAuthStore.getState().tenant;

        if (!tenantDb) {
          set(
            (state) => {
              if (!state.currentPatientId) return state;
              const cart = state.carts[state.currentPatientId];
              if (!cart) return state;
              return {
                ...state,
                carts: {
                  ...state.carts,
                  [state.currentPatientId]: {
                    ...cart,
                    error: "Tenant database not found",
                    loading: false,
                  },
                },
              };
            },
            undefined,
            "cart/loadCartFromServer - no tenant",
          );
          return;
        }

        try {
          const response = await getCart(ctxPatient, tenantDb);

          if (get().currentPatientId !== ctxPatient) return;

          const enrichItem = async (item: CartItem): Promise<CartItem> => {
            const enrichedItem: CartItem = { ...item };

            if (item.item_id && !item.cart_item_id) {
              enrichedItem.cart_item_id = item.item_id;
            }

            if (!enrichedItem.service_name && enrichedItem.service_id) {
              try {
                enrichedItem.service_name = await getServiceName(enrichedItem.service_id, tenantDb);
              } catch {
                enrichedItem.service_name = "Unknown Service";
              }
            }

            enrichedItem.points = Number(enrichedItem.points) || 0;
            enrichedItem.total_points = Number(enrichedItem.total_points) || 0;
            enrichedItem.quantity = Number(enrichedItem.quantity) || 0;

            const expectedTotalPoints = enrichedItem.points * enrichedItem.quantity;
            if (enrichedItem.total_points !== expectedTotalPoints && enrichedItem.quantity > 0) {
              captureException(
                new Error(
                  `Cart item ${getItemId(enrichedItem)} total_points mismatch. Expected: ${expectedTotalPoints}, Got: ${enrichedItem.total_points}`,
                ),
                {
                  tags: { context: "cart/loadCartFromServer", type: "data_validation" },
                  extra: {
                    cart_item_id: getItemId(enrichedItem),
                    service_id: enrichedItem.service_id,
                    points: enrichedItem.points,
                    quantity: enrichedItem.quantity,
                    expected_total_points: expectedTotalPoints,
                    actual_total_points: enrichedItem.total_points,
                  },
                },
              );
            }

            return enrichedItem;
          };

          const enrichedGroups = await Promise.all(
            (response.AuthGroups || []).map(async (group) => ({
              ...group,
              services: await Promise.all(group.services.map(enrichItem)),
            })),
          );

          const allItems = getAllItemsFromGroups(enrichedGroups);

          set(
            (state) => {
              if (!state.currentPatientId) return state;
              const existingCart = state.carts[state.currentPatientId];
              const hasItems = allItems.length > 0;
              const newPatientIdsWithCarts = new Set(state.patientIdsWithCarts);

              if (hasItems) {
                newPatientIdsWithCarts.add(state.currentPatientId);
              } else {
                newPatientIdsWithCarts.delete(state.currentPatientId);
              }

              savePatientIdsWithCarts(newPatientIdsWithCarts);

              return {
                ...state,
                patientIdsWithCarts: newPatientIdsWithCarts,
                carts: {
                  ...state.carts,
                  [state.currentPatientId]: {
                    ...existingCart,
                    authGroups: enrichedGroups,
                    loading: false,
                    error: null,
                  },
                },
              };
            },
            undefined,
            "cart/loadCartFromServer - success",
          );
        } catch (error) {
          const errorStatus =
            error && typeof error === "object" && "status" in error
              ? (error as { status?: number }).status
              : undefined;

          if (errorStatus === 404) {
            set(
              (state) => {
                if (!state.currentPatientId) return state;
                const cart = state.carts[state.currentPatientId];
                if (!cart) return state;
                const newPatientIdsWithCarts = new Set(state.patientIdsWithCarts);
                newPatientIdsWithCarts.delete(state.currentPatientId);
                savePatientIdsWithCarts(newPatientIdsWithCarts);

                const existingContext = loadCartContext();
                saveCartContext({
                  currentPatientId:
                    existingContext?.currentPatientId === state.currentPatientId
                      ? null
                      : (existingContext?.currentPatientId ?? null),
                });

                return {
                  ...state,
                  patientIdsWithCarts: newPatientIdsWithCarts,
                  carts: {
                    ...state.carts,
                    [state.currentPatientId]: {
                      ...cart,
                      authGroups: [],
                      loading: false,
                      error: null,
                    },
                  },
                };
              },
              undefined,
              "cart/loadCartFromServer - empty (404)",
            );
          } else {
            captureException(error, {
              tags: { context: "cart/loadCartFromServer" },
            });
            const errorMessage = error instanceof Error ? error.message : "Failed to load cart from server";
            set(
              (state) => {
                if (!state.currentPatientId) return state;
                const cart = state.carts[state.currentPatientId];
                if (!cart) return state;
                return {
                  ...state,
                  carts: {
                    ...state.carts,
                    [state.currentPatientId]: {
                      ...cart,
                      loading: false,
                      error: errorMessage,
                    },
                  },
                };
              },
              undefined,
              "cart/loadCartFromServer - error",
            );
          }
        }
      },

      getTotalPoints: () => {
        const currentCart = getCurrentCart(get());
        if (!currentCart) return 0;
        return currentCart.authGroups.reduce((sum, group) => sum + (group.total || 0), 0);
      },

      getItemCount: () => {
        const currentCart = getCurrentCart(get());
        if (!currentCart) return 0;
        const allItems = getAllItemsFromGroups(currentCart.authGroups);
        return allItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      },

      hasItems: () => {
        const currentCart = getCurrentCart(get());
        if (!currentCart) return false;
        return getAllItemsFromGroups(currentCart.authGroups).length > 0;
      },

      getCurrentCart: () => getCurrentCart(get()),

      getAllItems: () => {
        const currentCart = getCurrentCart(get());
        if (!currentCart) return [];
        return getAllItemsFromGroups(currentCart.authGroups);
      },

      getPatientId: () => get().currentPatientId,

      refreshAllCarts: async () => {
        const state = get();
        const tenantDb = useAuthStore.getState().tenant;
        if (!tenantDb) return;

        const patientIds = Array.from(state.patientIdsWithCarts);
        const refreshPromises = patientIds.map(async (patientId) => {
          try {
            const response = await getCart(patientId, tenantDb);

            const enrichedGroups = await Promise.all(
              (response.AuthGroups || []).map(async (group) => ({
                ...group,
                services: await Promise.all(
                  group.services.map(async (item) => {
                    const enrichedItem: CartItem = { ...item };

                    if (item.item_id && !item.cart_item_id) {
                      enrichedItem.cart_item_id = item.item_id;
                    }

                    if (!enrichedItem.service_name && enrichedItem.service_id) {
                      try {
                        enrichedItem.service_name = await getServiceName(enrichedItem.service_id, tenantDb);
                      } catch {
                        enrichedItem.service_name = "Unknown Service";
                      }
                    }

                    enrichedItem.points = Number(enrichedItem.points) || 0;
                    enrichedItem.total_points = Number(enrichedItem.total_points) || 0;
                    enrichedItem.quantity = Number(enrichedItem.quantity) || 0;

                    return enrichedItem;
                  }),
                ),
              })),
            );

            const allItems = getAllItemsFromGroups(enrichedGroups);

            set(
              (state) => {
                const existingCart = state.carts[patientId];
                const hasItems = allItems.length > 0;
                const newPatientIdsWithCarts = new Set(state.patientIdsWithCarts);

                if (hasItems) {
                  newPatientIdsWithCarts.add(patientId);
                } else {
                  newPatientIdsWithCarts.delete(patientId);
                }

                savePatientIdsWithCarts(newPatientIdsWithCarts);

                return {
                  ...state,
                  patientIdsWithCarts: newPatientIdsWithCarts,
                  carts: {
                    ...state.carts,
                    [patientId]: {
                      ...(existingCart || createEmptyCart()),
                      authGroups: enrichedGroups,
                      loading: false,
                      error: null,
                    },
                  },
                };
              },
              undefined,
              `cart/refreshAllCarts - ${patientId}`,
            );
          } catch (error) {
            captureException(error, {
              tags: { context: "cart/refreshAllCarts", patientId },
            });
          }
        });

        await Promise.all(refreshPromises);
      },

      getPatientIdsWithCarts: () => Array.from(get().patientIdsWithCarts),

      initializeCartsOnLogin: async () => {
        const state = get();
        const patientIds = Array.from(state.patientIdsWithCarts);
        if (patientIds.length > 0) {
          await get().refreshAllCarts();
        }
      },

      clearAllCarts: () => {
        clearCartContext();
        if (typeof window !== "undefined") {
          localStorage.removeItem(PATIENT_IDS_WITH_CARTS_KEY);
        }

        pendingQuantityUpdates.forEach((pending) => {
          clearTimeout(pending.timer);
        });
        pendingQuantityUpdates.clear();

        set(
          {
            carts: {},
            currentPatientId: null,
            patientIdsWithCarts: new Set<number>(),
            quantityUpdateError: null,
          },
          undefined,
          "cart/clearAllCarts",
        );
      },

      clearQuantityUpdateError: () => {
        set({ quantityUpdateError: null }, undefined, "cart/clearQuantityUpdateError");
      },
    }),
    {
      name: "cart-store",
    },
  ),
);
